import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import LandingPage from './pages/LandingPage';
import Inventory from './components/Inventory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send httpOnly auth cookie with every request
axios.defaults.withCredentials = true;

// =============================================================================
// AUTH CONTEXT
// =============================================================================
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setAdmin(res.data.admin);
      } catch (error) {
        // 401 is expected when there is no active session
        setAdmin(null);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setAdmin(res.data.admin);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ admin, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// =============================================================================
// LOGIN PAGE
// =============================================================================
const LoginPage = () => {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (admin) {
      navigate('/admin');
    }
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">BolkeHisaab</h1>
          <p className="text-gray-500">Admin Dashboard Login</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="admin@example.com"
              required
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
              required
              data-testid="login-password-input"
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="login-submit-btn"
          >
            {loading ? 'Please wait...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// MERCHANT CONTEXT - For merchant selection across the app
// =============================================================================
const MerchantContext = createContext(null);

const useMerchant = () => {
  const context = useContext(MerchantContext);
  if (!context) {
    throw new Error('useMerchant must be used within MerchantProvider');
  }
  return context;
};

const MerchantProvider = ({ children }) => {
  const { admin } = useAuth();
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!admin) {
      setLoading(false);
      return;
    }

    const fetchMerchants = async () => {
      try {
        const res = await axios.get(`${API}/merchants`);
        const merchantList = res.data.merchants || [];
        // Only show active merchants in dropdown
        const activeMerchants = merchantList.filter(m => m.status === 'ACTIVE');
        setMerchants(activeMerchants);
        
        // Auto-select first merchant or from localStorage
        const savedMerchantId = localStorage.getItem('selectedMerchantId');
        const savedMerchant = activeMerchants.find(m => m.id === savedMerchantId);
        
        if (savedMerchant) {
          setSelectedMerchant(savedMerchant);
        } else if (activeMerchants.length > 0) {
          setSelectedMerchant(activeMerchants[0]);
          localStorage.setItem('selectedMerchantId', activeMerchants[0].id);
        }
      } catch (error) {
        console.error('Error fetching merchants:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMerchants();
  }, [admin]);

  const selectMerchant = (merchant) => {
    setSelectedMerchant(merchant);
    localStorage.setItem('selectedMerchantId', merchant.id);
  };

  const refreshMerchants = async () => {
    try {
      const res = await axios.get(`${API}/merchants`);
      const merchantList = res.data.merchants || [];
      const activeMerchants = merchantList.filter(m => m.status === 'ACTIVE');
      setMerchants(activeMerchants);
    } catch (error) {
      console.error('Error refreshing merchants:', error);
    }
  };

  return (
    <MerchantContext.Provider value={{ merchants, selectedMerchant, selectMerchant, loading, refreshMerchants }}>
      {children}
    </MerchantContext.Provider>
  );
};

// =============================================================================
// MERCHANT SELECTOR COMPONENT
// =============================================================================
const MerchantSelector = () => {
  const { merchants, selectedMerchant, selectMerchant } = useMerchant();
  const [isOpen, setIsOpen] = useState(false);

  if (!selectedMerchant) {
    return (
      <div className="text-sm text-gray-500">No active merchants</div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
        data-testid="merchant-selector-btn"
      >
        <span className="text-sm font-medium">{selectedMerchant.business_name || selectedMerchant.name}</span>
        <span className="text-xs text-gray-500">{selectedMerchant.phone}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && merchants.length > 1 && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg z-50 border" data-testid="merchant-dropdown">
          <div className="p-2">
            <div className="text-xs text-gray-500 px-3 py-1 mb-1">Select Merchant</div>
            {merchants.map((merchant) => (
              <button
                key={merchant.id}
                onClick={() => {
                  selectMerchant(merchant);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 rounded hover:bg-gray-100 ${
                  selectedMerchant.id === merchant.id ? 'bg-blue-50 text-blue-700' : ''
                }`}
                data-testid={`merchant-option-${merchant.id}`}
              >
                <div className="text-sm font-medium">{merchant.business_name || merchant.name}</div>
                <div className="text-xs text-gray-500">{merchant.phone}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// COMPONENTS
// =============================================================================

const Dashboard = () => {
  const { selectedMerchant, loading: merchantLoading } = useMerchant();
  const { admin } = useAuth();
  const [stats, setStats] = useState({ transactions: 0, customers: 0, voiceLogs: 0, pendingAmount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedMerchant || !admin) return;
    
    const fetchStats = async () => {
      setLoading(true);
      try {
        const [txnRes, custRes, logsRes] = await Promise.all([
          axios.get(`${API}/merchants/${selectedMerchant.id}/transactions`),
          axios.get(`${API}/merchants/${selectedMerchant.id}/customers`),
          axios.get(`${API}/merchants/${selectedMerchant.id}/voice-logs`)
        ]);

        const transactions = txnRes.data.transactions || [];
        const customers = custRes.data.customers || [];
        const voiceLogs = logsRes.data.voice_logs || [];

        const pendingAmount = customers.reduce((sum, c) => sum + (c.running_balance || 0), 0);

        setStats({
          transactions: transactions.length,
          customers: customers.length,
          voiceLogs: voiceLogs.length,
          pendingAmount
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [selectedMerchant, admin]);

  if (merchantLoading) {
    return <div className="p-6 text-center">Loading merchants...</div>;
  }

  if (!selectedMerchant) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold mb-2">No Active Merchants</h2>
          <p className="text-gray-600 mb-4">
            Go to the Merchants tab to approve pending registrations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="dashboard">
      <h1 className="text-3xl font-bold mb-6">BolkeHisaab Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-100 p-6 rounded-lg shadow" data-testid="stat-transactions">
          <div className="text-blue-600 text-sm font-semibold">Total Transactions</div>
          <div className="text-3xl font-bold mt-2">{loading ? '...' : stats.transactions}</div>
        </div>
        
        <div className="bg-green-100 p-6 rounded-lg shadow" data-testid="stat-customers">
          <div className="text-green-600 text-sm font-semibold">Customers</div>
          <div className="text-3xl font-bold mt-2">{loading ? '...' : stats.customers}</div>
        </div>
        
        <div className="bg-purple-100 p-6 rounded-lg shadow" data-testid="stat-voice-logs">
          <div className="text-purple-600 text-sm font-semibold">Voice Notes Processed</div>
          <div className="text-3xl font-bold mt-2">{loading ? '...' : stats.voiceLogs}</div>
        </div>
        
        <div className="bg-red-100 p-6 rounded-lg shadow" data-testid="stat-pending">
          <div className="text-red-600 text-sm font-semibold">Total Pending</div>
          <div className="text-3xl font-bold mt-2">{loading ? '...' : `₹${stats.pendingAmount.toFixed(2)}`}</div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold mb-4">Welcome to BolkeHisaab!</h2>
        <p className="text-gray-600 mb-4">
          Voice-native bookkeeping platform for Indian MSMEs. 
          Powered by Sarvam AI (STT, LLM, TTS) and Twilio WhatsApp.
        </p>
        
        <div className="space-y-2">
          <div className="flex items-center">
            <span className="text-green-500 mr-2">✓</span>
            <span>Two-Stage AI Pipeline (Multilingual Normalizer + Transaction Extractor)</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-500 mr-2">✓</span>
            <span>WhatsApp Voice Note Processing (Telugu, Hindi, Dakhni, English)</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-500 mr-2">✓</span>
            <span>Automatic TTS Confirmations</span>
          </div>
          <div className="flex items-center">
            <span className="text-green-500 mr-2">✓</span>
            <span>Fuzzy Customer & Item Matching</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Transactions = () => {
  const { selectedMerchant } = useMerchant();
  const { admin } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [expandedTxn, setExpandedTxn] = useState(null);
  const [lineItems, setLineItems] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedMerchant || !admin) return;
    
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/merchants/${selectedMerchant.id}/transactions`);
        setTransactions(res.data.transactions || []);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [selectedMerchant, admin]);

  const fetchLineItems = async (transactionId) => {
    if (lineItems[transactionId]) {
      setExpandedTxn(expandedTxn === transactionId ? null : transactionId);
      return;
    }

    try {
      const res = await axios.get(`${API}/transactions/${transactionId}/details`);
      setLineItems(prev => ({
        ...prev,
        [transactionId]: res.data.line_items || []
      }));
      setExpandedTxn(transactionId);
    } catch (error) {
      console.error('Error fetching line items:', error);
    }
  };

  if (!selectedMerchant) {
    return <div className="p-6 text-center text-gray-500">Please select a merchant first.</div>;
  }

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6" data-testid="transactions-page">
      <h1 className="text-3xl font-bold mb-6">Transactions</h1>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Customer</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Amount</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Paid</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Pending</th>
              <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
              <th className="px-4 py-3 text-center text-sm font-semibold">Details</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((txn, idx) => (
              <React.Fragment key={txn.id}>
                <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`transaction-row-${txn.id}`}>
                  <td className="px-4 py-3 text-sm">
                    {new Date(txn.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">{txn.customer_name || 'Unknown'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs ${
                      txn.type === 'CREDIT_SALE' ? 'bg-orange-100 text-orange-700' :
                      txn.type === 'CASH_SALE' ? 'bg-green-100 text-green-700' :
                      txn.type === 'PAYMENT_RECEIVED' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {txn.type?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {txn.total_amount > 0 ? `₹${txn.total_amount?.toFixed(2)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {txn.amount_paid > 0 ? `₹${txn.amount_paid?.toFixed(2)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold">
                    {txn.amount_pending > 0 ? `₹${txn.amount_pending.toFixed(2)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      txn.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {txn.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    <button
                      onClick={() => fetchLineItems(txn.id)}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                      data-testid={`view-items-btn-${txn.id}`}
                    >
                      {expandedTxn === txn.id ? 'Hide' : 'View Items'}
                    </button>
                  </td>
                </tr>
                {expandedTxn === txn.id && lineItems[txn.id] && (
                  <tr className="bg-blue-50">
                    <td colSpan="8" className="px-4 py-4">
                      <div className="bg-white rounded p-4 shadow-inner">
                        <h3 className="font-semibold mb-3 text-gray-700">Line Items:</h3>
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left">Item</th>
                              <th className="px-3 py-2 text-left">Brand</th>
                              <th className="px-3 py-2 text-right">Quantity</th>
                              <th className="px-3 py-2 text-left">Unit</th>
                              <th className="px-3 py-2 text-right">Unit Price</th>
                              <th className="px-3 py-2 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineItems[txn.id].map((item, i) => (
                              <tr key={item.id || `${txn.id}-item-${i}`} className="border-t">
                                <td className="px-3 py-2">
                                  {item.item_name}
                                  {item.original_name && item.original_name !== item.item_name && (
                                    <span className="text-gray-500 text-xs ml-1">({item.original_name})</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-gray-600">{item.brand || '-'}</td>
                                <td className="px-3 py-2 text-right font-medium">{item.quantity}</td>
                                <td className="px-3 py-2">{item.unit}</td>
                                <td className="px-3 py-2 text-right">
                                  {item.unit_price ? `₹${item.unit_price.toFixed(2)}` : '-'}
                                </td>
                                <td className="px-3 py-2 text-right font-semibold">
                                  {item.line_total ? `₹${item.line_total.toFixed(2)}` : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        {transactions.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No transactions yet. Send a voice note to create one!
          </div>
        )}
      </div>
    </div>
  );
};

const Customers = () => {
  const { selectedMerchant } = useMerchant();
  const { admin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedMerchant || !admin) return;
    
    const fetchCustomers = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/merchants/${selectedMerchant.id}/customers`);
        setCustomers(res.data.customers || []);
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [selectedMerchant, admin]);

  if (!selectedMerchant) {
    return <div className="p-6 text-center text-gray-500">Please select a merchant first.</div>;
  }

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6" data-testid="customers-page">
      <h1 className="text-3xl font-bold mb-6">Customers</h1>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Phone</th>
              <th className="px-4 py-3 text-right text-sm font-semibold">Running Balance</th>
              <th className="px-4 py-3 text-center text-sm font-semibold">Trust Score</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Added On</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer, idx) => (
              <tr key={customer.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`customer-row-${customer.id}`}>
                <td className="px-4 py-3 text-sm font-medium">{customer.name}</td>
                <td className="px-4 py-3 text-sm">{customer.phone || '-'}</td>
                <td className="px-4 py-3 text-sm text-right font-semibold">
                  <span className={customer.running_balance > 0 ? 'text-red-600' : 'text-green-600'}>
                    ₹{customer.running_balance?.toFixed(2)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-center">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                    {customer.trust_score || 50}/100
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {new Date(customer.created_at).toLocaleDateString('en-IN')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {customers.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No customers yet.
          </div>
        )}
      </div>
    </div>
  );
};

const VoiceLogs = () => {
  const { selectedMerchant } = useMerchant();
  const { admin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedMerchant || !admin) return;
    
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/merchants/${selectedMerchant.id}/voice-logs`);
        setLogs(res.data.voice_logs || []);
      } catch (error) {
        console.error('Error fetching voice logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [selectedMerchant, admin]);

  if (!selectedMerchant) {
    return <div className="p-6 text-center text-gray-500">Please select a merchant first.</div>;
  }

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6" data-testid="voice-logs-page">
      <h1 className="text-3xl font-bold mb-6">Voice Processing Logs</h1>
      
      <div className="space-y-4">
        {logs.map((log) => (
          <div key={log.id} className="bg-white rounded-lg shadow p-4" data-testid={`voice-log-${log.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  log.message_type === 'bill_photo' ? 'bg-purple-100 text-purple-700' :
                  log.message_type === 'text' ? 'bg-blue-100 text-blue-700' :
                  'bg-emerald-100 text-emerald-700'
                }`} data-testid={`log-type-badge-${log.id}`}>
                  {log.message_type === 'bill_photo' ? '📷 Bill Photo' : log.message_type === 'text' ? '💬 Text' : '🎤 Voice'}
                </span>
                <span className="text-sm text-gray-500">Channel: {log.channel}</span>
                <span className="mx-1">|</span>
                <span className="text-sm text-gray-500">
                  {new Date(log.created_at).toLocaleString('en-IN')}
                </span>
              </div>
              <span className={`px-3 py-1 rounded text-xs font-semibold ${
                log.processing_status === 'completed' ? 'bg-green-100 text-green-700' :
                log.processing_status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                log.processing_status === 'failed' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {log.processing_status}
              </span>
            </div>
            
            {log.raw_transcript && (
              <div className="mt-3">
                <div className="text-xs text-gray-500 mb-1">Raw Transcript ({log.language_detected}):</div>
                <div className="text-sm bg-gray-50 p-2 rounded">{log.raw_transcript}</div>
              </div>
            )}
            
            {log.normalized_form && (
              <div className="mt-2">
                <div className="text-xs text-gray-500 mb-1">Normalized Form:</div>
                <div className="text-sm bg-blue-50 p-2 rounded text-blue-900">{log.normalized_form}</div>
              </div>
            )}
            
            {log.bill_extraction && log.bill_extraction.items && (
              <div className="mt-2" data-testid={`bill-extraction-${log.id}`}>
                <div className="text-xs text-gray-500 mb-1">
                  Extracted Bill — {log.bill_extraction.supplier_name || 'Unknown Supplier'}
                  {log.bill_extraction.grand_total ? ` | Total: ₹${log.bill_extraction.grand_total}` : ''}
                </div>
                <div className="text-sm bg-purple-50 p-2 rounded text-purple-900">
                  {log.bill_extraction.items.map((item, i) => (
                    <div key={`${log.id}-bill-${item.product_name}-${i}`} className="flex justify-between py-0.5">
                      <span>{item.brand ? `${item.brand} ` : ''}{item.product_name}</span>
                      <span className="text-purple-600">{item.qty} {item.unit_type}{item.unit_price ? ` @ ₹${item.unit_price}` : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {log.error_message && (
              <div className="mt-2">
                <div className="text-xs text-red-500 mb-1">Error:</div>
                <div className="text-sm bg-red-50 p-2 rounded text-red-700">{log.error_message}</div>
              </div>
            )}
          </div>
        ))}
        
        {logs.length === 0 && (
          <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
            No voice logs yet. Send a voice note to WhatsApp to see it here!
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// MERCHANT MANAGEMENT (Admin)
// =============================================================================

const MerchantManagement = () => {
  const { admin } = useAuth();
  const { refreshMerchants } = useMerchant();
  const [allMerchants, setAllMerchants] = useState([]);
  const [pendingMerchants, setPendingMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, pendingRes] = await Promise.all([
        axios.get(`${API}/merchants`),
        axios.get(`${API}/admin/pending-merchants`)
      ]);
      setAllMerchants(allRes.data.merchants || []);
      setPendingMerchants(pendingRes.data.pending_merchants || []);
    } catch (error) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (admin) {
      fetchMerchants();
    }
  }, [admin, fetchMerchants]);

  const handleAction = async (merchantId, action) => {
    setActionLoading(merchantId);
    try {
      await axios.post(`${API}/admin/merchants/${merchantId}/${action}`, {});
      await fetchMerchants();
      await refreshMerchants();
    } catch (error) {
      console.error(`Error ${action} merchant:`, error);
      alert(`Failed to ${action} merchant: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'ACTIVE': 'bg-green-100 text-green-700',
      'PENDING_APPROVAL': 'bg-yellow-100 text-yellow-700',
      'REJECTED': 'bg-red-100 text-red-700',
      'SUSPENDED': 'bg-orange-100 text-orange-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6" data-testid="merchant-management">
      <h1 className="text-3xl font-bold mb-6">Merchant Management</h1>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-lg font-medium ${
            activeTab === 'pending' 
              ? 'bg-yellow-500 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          data-testid="tab-pending"
        >
          Pending Approval ({pendingMerchants.length})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-lg font-medium ${
            activeTab === 'all' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          data-testid="tab-all"
        >
          All Merchants ({allMerchants.length})
        </button>
      </div>

      {/* Pending Approvals Tab */}
      {activeTab === 'pending' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {pendingMerchants.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No pending approvals
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-yellow-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Business</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Phone</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Registered</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingMerchants.map((merchant, idx) => (
                  <tr key={merchant.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`pending-row-${merchant.id}`}>
                    <td className="px-4 py-3 text-sm font-medium">{merchant.name}</td>
                    <td className="px-4 py-3 text-sm">{merchant.business_name}</td>
                    <td className="px-4 py-3 text-sm">{merchant.phone}</td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(merchant.created_at).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-center space-x-2">
                      <button
                        onClick={() => handleAction(merchant.id, 'approve')}
                        disabled={actionLoading === merchant.id}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-sm"
                        data-testid={`approve-btn-${merchant.id}`}
                      >
                        {actionLoading === merchant.id ? '...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleAction(merchant.id, 'reject')}
                        disabled={actionLoading === merchant.id}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 text-sm"
                        data-testid={`reject-btn-${merchant.id}`}
                      >
                        {actionLoading === merchant.id ? '...' : 'Reject'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* All Merchants Tab */}
      {activeTab === 'all' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Business</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Phone</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Registered</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allMerchants.map((merchant, idx) => (
                <tr key={merchant.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`merchant-row-${merchant.id}`}>
                  <td className="px-4 py-3 text-sm font-medium">{merchant.name}</td>
                  <td className="px-4 py-3 text-sm">{merchant.business_name}</td>
                  <td className="px-4 py-3 text-sm">{merchant.phone}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(merchant.status)}`}>
                      {merchant.status || 'ACTIVE'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(merchant.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-center space-x-1">
                    {merchant.status === 'PENDING_APPROVAL' && (
                      <>
                        <button
                          onClick={() => handleAction(merchant.id, 'approve')}
                          disabled={actionLoading === merchant.id}
                          className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-xs"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleAction(merchant.id, 'reject')}
                          disabled={actionLoading === merchant.id}
                          className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 text-xs"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {merchant.status === 'ACTIVE' && (
                      <button
                        onClick={() => handleAction(merchant.id, 'suspend')}
                        disabled={actionLoading === merchant.id}
                        className="px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 text-xs"
                      >
                        Suspend
                      </button>
                    )}
                    {(merchant.status === 'SUSPENDED' || merchant.status === 'REJECTED') && (
                      <button
                        onClick={() => handleAction(merchant.id, 'activate')}
                        disabled={actionLoading === merchant.id}
                        className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-xs"
                      >
                        Activate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {allMerchants.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No merchants registered yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Inventory Page - wrapper to use merchant context
const InventoryPage = () => {
  const { selectedMerchant } = useMerchant();
  return <Inventory merchantId={selectedMerchant?.id} />;
};

// =============================================================================
// MAIN APP
// =============================================================================

const AppLayout = () => {
  const { admin, logout } = useAuth();

  return (
    <MerchantProvider>
      <div className="min-h-screen bg-gray-100">
        {/* Navigation */}
        <nav className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between h-16">
              <div className="flex space-x-8">
                <Link to="/admin" className="flex items-center px-3 py-2 text-gray-700 hover:text-blue-600 font-semibold" data-testid="nav-dashboard">
                  Dashboard
                </Link>
                <Link to="/admin/transactions" className="flex items-center px-3 py-2 text-gray-700 hover:text-blue-600" data-testid="nav-transactions">
                  Transactions
                </Link>
                <Link to="/admin/customers" className="flex items-center px-3 py-2 text-gray-700 hover:text-blue-600" data-testid="nav-customers">
                  Customers
                </Link>
                <Link to="/admin/voice-logs" className="flex items-center px-3 py-2 text-gray-700 hover:text-blue-600" data-testid="nav-voice-logs">
                  Voice Logs
                </Link>
                <Link to="/admin/merchants" className="flex items-center px-3 py-2 text-gray-700 hover:text-blue-600 font-medium" data-testid="nav-merchants">
                  Merchants
                </Link>
                <Link to="/admin/inventory" className="flex items-center px-3 py-2 text-gray-700 hover:text-blue-600" data-testid="nav-inventory">
                  Inventory
                </Link>
                <Link to="/admin/feedback" className="flex items-center px-3 py-2 text-gray-700 hover:text-blue-600" data-testid="nav-feedback">
                  Feedback
                </Link>
              </div>
              <div className="flex items-center space-x-4">
                <MerchantSelector />
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span>{admin?.name}</span>
                  <button
                    onClick={logout}
                    className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                    data-testid="logout-btn"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Routes */}
        <div className="max-w-7xl mx-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="customers" element={<Customers />} />
            <Route path="voice-logs" element={<VoiceLogs />} />
            <Route path="merchants" element={<MerchantManagement />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="feedback" element={<FeedbackAdmin />} />
          </Routes>
        </div>
      </div>
    </MerchantProvider>
  );
};

// =============================================================================
// FEEDBACK ADMIN PAGE
// =============================================================================
const FeedbackAdmin = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const res = await axios.get(`${API}/admin/feedback`);
        setFeedback(res.data.feedback || []);
      } catch (err) {
        setError('Failed to load feedback');
        console.error('Failed to load feedback:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, []);

  const renderStars = (rating) => (
    <span className="text-amber-500" title={`${rating}/5`}>
      {'★'.repeat(rating)}
      <span className="text-gray-300">{'★'.repeat(5 - rating)}</span>
    </span>
  );

  return (
    <div className="p-6" data-testid="feedback-admin-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Website Feedback</h2>
        <span className="text-sm text-gray-500" data-testid="feedback-count">
          {loading ? 'Loading...' : `${feedback.length} submission${feedback.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-4" data-testid="feedback-error">{error}</div>
      )}

      {!loading && !error && feedback.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500" data-testid="feedback-empty">
          No feedback submitted yet
        </div>
      )}

      <div className="space-y-4">
        {feedback.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow p-5" data-testid={`feedback-card-${item.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <div>
                <span className="font-semibold text-gray-800">{item.name}</span>
                {item.contact && (
                  <span className="ml-3 text-sm text-gray-500">{item.contact}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg">{renderStars(item.rating)}</span>
                <span className="text-xs text-gray-400">
                  {item.created_at ? new Date(item.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                </span>
              </div>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{item.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};


function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Landing Page */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Admin Login */}
          <Route path="/admin/login" element={<LoginPage />} />
          
          {/* Legacy login redirect */}
          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          
          {/* Protected Admin Dashboard */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
